import React, { useRef, useState } from 'react';
import TurndownService from 'turndown';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { cn } from '../utils/cn';

const { FiPlus, FiClipboard, FiInfo, FiZap } = FiIcons;

const turndownService = new TurndownService({ headingStyle: 'atx', codeBlockStyle: 'fenced' });

const TEMPLATES = [
  { label: 'Chapter', icon: '📖', content: '## Chapter Title\n\nWrite your chapter content here...' },
  { label: 'Intro', icon: '👋', content: '## Introduction\n\nWelcome to this book. In this chapter, we will explore...' },
  { label: 'Conclusion', icon: '🏁', content: '## Conclusion\n\nIn summary, we covered...\n\n### Key Takeaways\n\n- Point one\n- Point two\n- Point three' },
  { label: 'Quote', icon: '💬', content: '> "Insert a meaningful quote here."\n>\n> — Author Name' },
];

export default function DraftArea({ onAddPart }) {
  const [text, setText] = useState('');
  const [isPasting, setIsPasting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const textareaRef = useRef(null);

  const handleChange = (e) => {
    setText(e.target.value);
    setCharCount(e.target.value.length);
  };

  const handlePaste = (e) => {
    const html = e.clipboardData.getData('text/html');
    if (html) {
      e.preventDefault();
      setIsPasting(true);
      const markdown = turndownService.turndown(html);
      const target = e.target;
      const start = target.selectionStart;
      const end = target.selectionEnd;
      const newValue = text.substring(0, start) + markdown + text.substring(end);
      setText(newValue);
      setCharCount(newValue.length);
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.selectionStart = textareaRef.current.selectionEnd = start + markdown.length;
        }
        setIsPasting(false);
      }, 10);
    }
  };

  const handleSubmit = () => {
    if (!text.trim()) return;
    onAddPart(text);
    setText('');
    setCharCount(0);
  };

  const handleTemplate = (tpl) => {
    setText(tpl.content);
    setCharCount(tpl.content.length);
    textareaRef.current?.focus();
  };

  const wordCount = text.trim() === '' ? 0 : text.split(/\s+/).filter(Boolean).length;

  return (
    <div className="flex flex-col h-full bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="bg-slate-50 border-b border-slate-200 px-4 py-2.5 flex items-center justify-between shrink-0">
        <h2 className="text-xs font-black text-slate-600 uppercase tracking-widest flex items-center">
          <SafeIcon icon={FiClipboard} className="mr-2 text-slate-400" />
          Drafting Pad
        </h2>
        <div className="group relative flex items-center">
          <SafeIcon icon={FiInfo} className="text-slate-400 text-sm cursor-help" />
          <div className="absolute right-0 w-64 p-2.5 bg-slate-800 text-xs text-slate-200 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all top-6 z-20 pointer-events-none">
            Paste rich text (HTML) from any website — it auto-converts to clean Markdown.
          </div>
        </div>
      </div>

      {/* Quick Templates */}
      <div className="px-3 pt-3 pb-2 shrink-0 border-b border-slate-100">
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center">
          <SafeIcon icon={FiZap} className="mr-1" /> Quick Templates
        </p>
        <div className="flex flex-wrap gap-1.5">
          {TEMPLATES.map(tpl => (
            <button
              key={tpl.label}
              onClick={() => handleTemplate(tpl)}
              className="flex items-center gap-1 px-2.5 py-1 bg-slate-100 hover:bg-indigo-50 hover:text-indigo-700 text-slate-600 text-xs font-semibold rounded-md border border-slate-200 hover:border-indigo-200 transition-all"
            >
              <span>{tpl.icon}</span>
              <span>{tpl.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Textarea — flex-1 fills all remaining space */}
      <div className="flex-1 min-h-0 p-3">
        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleChange}
          onPaste={handlePaste}
          placeholder="Paste or type your content here. Rich text auto-converts to Markdown..."
          className={cn(
            "w-full h-full resize-none outline-none text-slate-700 font-mono text-sm leading-relaxed",
            "placeholder:text-slate-400 placeholder:font-sans placeholder:text-sm",
            isPasting && "opacity-50"
          )}
        />
      </div>

      {/* Footer */}
      <div className="bg-slate-50 border-t border-slate-200 px-4 py-3 shrink-0">
        <div className="flex items-center justify-between mb-2.5">
          <span className="text-[11px] text-slate-400 font-mono">
            {wordCount.toLocaleString()} words · {charCount.toLocaleString()} chars
          </span>
          {text.trim() && (
            <span className="text-[10px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full border border-green-200">
              Ready to add
            </span>
          )}
        </div>
        <button
          onClick={handleSubmit}
          disabled={!text.trim()}
          className="w-full flex items-center justify-center space-x-2 py-2.5 px-4 bg-slate-900 hover:bg-indigo-700 text-white rounded-lg font-bold text-sm transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98]"
        >
          <SafeIcon icon={FiPlus} />
          <span>Add Part to Book</span>
        </button>
      </div>
    </div>
  );
}